import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Fragment } from 'react/jsx-runtime'

import { zodResolver } from '@hookform/resolvers/zod'

import { useCallMutation } from '@/common/api/client'
import { apiPostFetcher, apiPutFetcher } from '@/common/api/fetcher'
import { useAnalysisTargetDeleteApi } from '@/common/api/modules/mediaAnalysisTarget'
import { useSiteGetApi } from '@/common/api/modules/site'
import {
  CreateAnalysisTargetResponse,
  CreateAnalysisTargetVars,
  UpdateAnalysisTargetResponse,
  UpdateAnalysisTargetVars
} from '@/common/api/params/mediaAnalysisTarget'
import { ApiPath } from '@/common/api/path'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import { MAX_COMPETITORS } from '@/common/app/modules/media/shareOfVoice'
import { analysisTargetEditPath, analysisTargetPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { useShowSavedToast } from '@/common/app/toast'
import { MediaAnalysisTarget } from '@/common/model/media/mediaAnalysisTarget'
import { preventKeyDown } from '@/common/util/keyboard'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import { FieldType, TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import Content from '@/components/view/layouts/page/Content'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  AnalysisTargetFormSchemaType,
  MAX_COMPETITOR_DOMAINS,
  analysisTargetNameLabelMapping,
  analysisTargetSchema,
  convertAnalysisTargetSchemaToVars,
  convertAnalysisTargetToSchema,
  makeDefaultAnalysisTargetSchema
} from '@/components/container/form/modules/media/analysisTarget/analysisTargetSchema'
import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormTextField from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'
import AppTitleBadge, { AppTitleBadgeType } from '@/components/container/layout/AppTitleBadge'

interface Props {
  target?: MediaAnalysisTarget
  onRefetch?(): void
}

const AnalysisTargetForm = ({ target, onRefetch }: Props) => {
  const navigate = useNavigate()
  const siteId = useSiteId()
  const { data: site } = useSiteGetApi({ site_id: siteId })
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty, isSubmitting },
    reset
  } = useForm<AnalysisTargetFormSchemaType>({
    resolver: zodResolver(analysisTargetSchema),
    values: target
      ? convertAnalysisTargetToSchema(target)
      : makeDefaultAnalysisTargetSchema(siteId),
    mode: 'onChange'
  })
  const { fields, append, remove } = useFieldArray({ control, name: 'competitorBrands' })

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, analysisTargetNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: !!target })

  const { trigger } = useCallMutation<
    CreateAnalysisTargetResponse | UpdateAnalysisTargetResponse,
    CreateAnalysisTargetVars | UpdateAnalysisTargetVars
  >(
    target ? ApiPath.UPDATE_ANALYSIS_TARGET : ApiPath.CREATE_ANALYSIS_TARGET,
    {
      onSuccess: (response) => {
        showSaved(response.name)
        target && onRefetch ? onRefetch() : navigate(analysisTargetEditPath(siteId, response.id))
      }
    },
    target ? apiPutFetcher : apiPostFetcher
  )

  const onSubmit: SubmitHandler<AnalysisTargetFormSchemaType> = (data) =>
    trigger(convertAnalysisTargetSchemaToVars(data))

  // 削除（編集時のみ）
  const { onClickDelete } = useAnalysisTargetDeleteApi(target ?? ({} as MediaAnalysisTarget), () =>
    navigate(analysisTargetPath(siteId))
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventKeyDown}>
      <PageTitle
        title={target ? target.name : '分析対象を追加する'}
        badgeNode={<AppTitleBadge type={AppTitleBadgeType.MEDIA} />}
      />
      {isDirty && (
        <TopBar
          status={target ? TopBarStatus.CHANGE : TopBarStatus.NEW}
          captionText={target ? '未反映の変更があります' : '登録されていない新しい分析対象'}
          onCancel={() =>
            reset(
              target
                ? convertAnalysisTargetToSchema(target)
                : makeDefaultAnalysisTargetSchema(siteId)
            )
          }
          loading={isSubmitting}
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <Content>
        <Area noBorder>
          <Heading>基本設定</Heading>
          <AreaContent>
            <FormRow>
              <FormLabel required id='name'>
                分析対象サイト名
              </FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                <FormTextField
                  register={register('name')}
                  fieldType={FieldType.REQUIRED}
                  error={!!errors.name?.message}
                  size={TextBoxSize.FULL}
                  placeholder='競合A社'
                />
                <FormError message={errors.name?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel required id='siteUrl'>
                サイトURL
              </FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                <FormTextField
                  register={register('siteUrl')}
                  fieldType={FieldType.REQUIRED}
                  error={!!errors.siteUrl?.message}
                  size={TextBoxSize.FULL}
                  placeholder='https://www.example.com/'
                />
                <FormError message={errors.siteUrl?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel subText='AI推定用'>キーワードフィルタ</FormLabel>
              <FormRow direction={'vertical'} gap={4}>
                <FormTextField
                  id='keywordInclude'
                  register={register('keywordInclude')}
                  error={!!errors.keywordInclude?.message}
                  size={TextBoxSize.FULL}
                  placeholder='例）家具,インテリア'
                  label='含む'
                />
                <FormError message={errors.keywordInclude?.message} />
                <FormTextField
                  id='keywordExclude'
                  register={register('keywordExclude')}
                  error={!!errors.keywordExclude?.message}
                  size={TextBoxSize.FULL}
                  placeholder='例）中古,アウトレット'
                  label='含まない'
                />
                <FormError message={errors.keywordExclude?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel subText={`最大${MAX_COMPETITOR_DOMAINS}つ`}>競合ドメイン</FormLabel>
              <FormRow direction={'vertical'} gap={4}>
                {Array.from({ length: MAX_COMPETITOR_DOMAINS }).map((_, idx) => (
                  <Fragment key={idx}>
                    <FormTextField
                      register={register(`competitorDomains.${idx}`)}
                      error={!!errors.competitorDomains?.[idx]?.message}
                      size={TextBoxSize.FULL}
                      placeholder={`competitor-${idx + 1}.example.com`}
                      label={`競合${idx + 1}`}
                    />

                    <FormError message={errors.competitorDomains?.[idx]?.message} />
                  </Fragment>
                ))}
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
        {site?.enable_geo_llmo && (
          <Area noBorder>
            <Heading>GEO/LLMO分析用設定</Heading>
            <AreaContent>
              <FormRow>
                <FormLabel id='ownBrand'>自社ブランド</FormLabel>
                <FormRow direction={'vertical'} gap={8}>
                  <FormTextField
                    register={register('ownBrand')}
                    error={!!errors.ownBrand?.message}
                    size={TextBoxSize.FULL}
                    placeholder='自社ブランド名を入力'
                    label='ブランド名'
                  />
                  <FormError message={errors.ownBrand?.message} />
                  <FormTextField
                    id='ownUrl'
                    register={register('ownUrl')}
                    error={!!errors.ownUrl?.message}
                    size={TextBoxSize.FULL}
                    placeholder='https://www.example.com/'
                    label='ブランドサイトTOPページURL'
                  />
                  <FormError message={errors.ownUrl?.message} />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel id='category'>商品/サービスカテゴリ</FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormTextField
                    register={register('category')}
                    error={!!errors.category?.message}
                    size={TextBoxSize.FULL}
                    placeholder='例）ECサイト構築'
                  />
                  <FormError message={errors.category?.message} />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel>競合ブランド（最大{MAX_COMPETITORS}社）</FormLabel>
                <FormRow direction={'vertical'} gap={8}>
                  {fields.map((field, idx) => (
                    <div key={field.id} className='flex flex-col gap-1.5'>
                      <div className='flex items-start gap-1.5'>
                        <div className='flex-1'>
                          <FormTextField
                            register={register(`competitorBrands.${idx}.brand`)}
                            error={!!errors.competitorBrands?.[idx]?.brand}
                            size={TextBoxSize.FULL}
                            placeholder={`競合${idx + 1} ブランド名`}
                          />
                        </div>
                        <div className='flex-1'>
                          <FormTextField
                            register={register(`competitorBrands.${idx}.url`)}
                            error={!!errors.competitorBrands?.[idx]?.url}
                            size={TextBoxSize.FULL}
                            placeholder={`競合${idx + 1} URL`}
                          />
                        </div>
                        {fields.length > 1 && (
                          <Button color={ButtonColor.CRITICAL} onClick={() => remove(idx)}>
                            削除
                          </Button>
                        )}
                      </div>
                      <FormError
                        message={
                          errors.competitorBrands?.[idx]?.brand?.message ||
                          errors.competitorBrands?.[idx]?.url?.message
                        }
                      />
                    </div>
                  ))}
                  {fields.length < MAX_COMPETITORS && (
                    <div>
                      <Button
                        color={ButtonColor.SECONDARY}
                        onClick={() => append({ brand: '', url: '' })}
                      >
                        + 競合を追加
                      </Button>
                    </div>
                  )}
                </FormRow>
              </FormRow>
            </AreaContent>
          </Area>
        )}
      </Content>
      <PageBottom between={!!target}>
        {target && (
          <Button color={ButtonColor.CRITICAL} size='large' onClick={onClickDelete}>
            この分析対象を削除する
          </Button>
        )}
        <Button type='submit' size='large' color={ButtonColor.PRIMARY_BRAND} loading={isSubmitting}>
          {target ? '保存する' : '登録する'}
        </Button>
      </PageBottom>
    </form>
  )
}

export default AnalysisTargetForm
